import {
  Box,
  IconButton,
  Paper,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  createContext,
  ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTenantTranslation } from "hooks/formatters";

export type FloatingPanelSize = Readonly<{ width: number; height: number }>;
export type FloatingPanelPosition = Readonly<{ x: number; y: number }>;
export type FloatingPanelPlacement = Readonly<{
  position: FloatingPanelPosition;
  opacity: number;
}>;

type FloatingPanelProps = Readonly<{
  children: ReactNode;
  title: string;
  initialPosition: FloatingPanelPosition;
  // Omit to fit the panel to its content until the user resizes it.
  initialSize?: FloatingPanelSize;
  initialOpacity?: number;
  onClose: (placement: FloatingPanelPlacement) => void;
}>;

type DialogCloseReason = "backdropClick" | "escapeKeyDown";

export type FloatingPanelStack = Readonly<{
  register: (close: () => void) => () => void;
  closePanelOnEscapeFirst: (
    onClose: () => void
  ) => (event: unknown, reason: DialogCloseReason) => void;
}>;

export const useFloatingPanelStack = (): FloatingPanelStack => {
  const closersRef = useRef<readonly (() => void)[]>([]);
  return useMemo(
    () => ({
      register: (close) => {
        closersRef.current = [...closersRef.current, close];
        return () => {
          closersRef.current = closersRef.current.filter(
            (closer) => closer !== close
          );
        };
      },
      closePanelOnEscapeFirst: (onClose) => (_event, reason) => {
        const newestPanelClose = closersRef.current.at(-1);
        if (reason === "escapeKeyDown" && newestPanelClose) {
          newestPanelClose();
          return;
        }
        onClose();
      },
    }),
    []
  );
};

type FloatingPanelContainerValue = Readonly<{
  element: HTMLElement | null;
  stack: FloatingPanelStack;
}>;

const FloatingPanelContainerContext =
  createContext<FloatingPanelContainerValue | null>(null);

export const FloatingPanelContainer = ({
  stack,
  children,
}: Readonly<{ stack: FloatingPanelStack; children: ReactNode }>) => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ element, stack }), [element, stack]);
  return (
    <FloatingPanelContainerContext value={value}>
      {children}
      <div ref={setElement} />
    </FloatingPanelContainerContext>
  );
};

const DEFAULT_OPACITY = 0.85;
const MIN_WIDTH = 250;
const MIN_HEIGHT = 200;
const FIT_CONTENT_MAX_WIDTH = "80vw";
const FIT_CONTENT_MAX_HEIGHT = "70vh";

export const FloatingPanel = ({
  children,
  title,
  initialPosition,
  initialSize,
  initialOpacity,
  onClose,
}: FloatingPanelProps) => {
  const { t } = useTenantTranslation();
  const container = use(FloatingPanelContainerContext);
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState<FloatingPanelSize | null>(() =>
    initialSize === undefined
      ? null
      : {
          width: Math.max(MIN_WIDTH, initialSize.width),
          height: Math.max(MIN_HEIGHT, initialSize.height),
        }
  );
  const [opacity, setOpacity] = useState(initialOpacity ?? DEFAULT_OPACITY);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const bodyRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      dragOffsetRef.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        setPosition({
          x: moveEvent.clientX - dragOffsetRef.current.x,
          y: moveEvent.clientY - dragOffsetRef.current.y,
        });
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [position]
  );

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsResizing(true);
      const startSize = size ?? bodyRef.current?.getBoundingClientRect();
      resizeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: startSize?.width ?? MIN_WIDTH,
        height: startSize?.height ?? MIN_HEIGHT,
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - resizeStartRef.current.x;
        const deltaY = moveEvent.clientY - resizeStartRef.current.y;

        setSize({
          width: Math.max(MIN_WIDTH, resizeStartRef.current.width + deltaX),
          height: Math.max(MIN_HEIGHT, resizeStartRef.current.height + deltaY),
        });
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [size]
  );

  const close = () => {
    onClose({ position, opacity });
  };
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  });

  const stack = container?.stack;
  useEffect(
    () =>
      stack?.register(() => {
        closeRef.current();
      }),
    [stack]
  );

  useEffect(() => {
    paperRef.current?.focus({ preventScroll: true });
  }, []);

  return createPortal(
    <Paper
      ref={paperRef}
      tabIndex={-1}
      role="dialog"
      aria-label={title}
      elevation={8}
      sx={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 1300,
        opacity,
        borderRadius: 2,
        overflow: "hidden",
        cursor: isDragging ? "grabbing" : "default",
        display: "flex",
        flexDirection: "column",
        "&:focus": { outline: "none" },
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          close();
        }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1,
          py: 0.5,
          bgcolor: "grey.800",
          cursor: "grab",
          flexShrink: 0,
        }}
        onMouseDown={handleMouseDown}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <DragIndicatorIcon fontSize="small" sx={{ color: "grey.400" }} />
          <Typography
            variant="caption"
            sx={{ color: "grey.300", fontWeight: 600, userSelect: "none" }}
          >
            {title}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          aria-label={t("close")}
          onClick={close}
          sx={{ color: "grey.400", p: 0.25 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Box
        ref={bodyRef}
        sx={{
          p: 1,
          overflow: "auto",
          minHeight: 0,
          ...(size === null
            ? {
                maxWidth: FIT_CONTENT_MAX_WIDTH,
                maxHeight: FIT_CONTENT_MAX_HEIGHT,
              }
            : { width: size.width, height: size.height }),
        }}
      >
        {children}
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, pb: 1, flexShrink: 0 }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
        >
          {t("opacity")}
        </Typography>
        <Slider
          size="small"
          value={opacity}
          onChange={(_, value) => {
            setOpacity(value);
          }}
          min={0.3}
          max={1}
          step={0.1}
          sx={{ width: 100 }}
        />
      </Stack>
      <Box
        onMouseDown={handleResizeMouseDown}
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: 16,
          height: 16,
          cursor: "nwse-resize",
          "&::before": {
            content: '""',
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 14,
            height: 14,
            bgcolor: isResizing ? "grey.600" : "grey.500",
            clipPath: "polygon(100% 0%, 100% 100%, 0% 100%)",
          },
        }}
      />
    </Paper>,
    container?.element ?? document.body
  );
};
